import React, { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import FormGroup from 'components/ui/FormGroup/FormGroup';
import Button from 'components/ui/Button/Button';
import { CredentialsModel } from 'ts/shared/classes/CredentialsModel';
import { credentialsValidation as validation } from 'constants/constant';
import { login } from 'services/authorize';
import { useLocation, useNavigate } from 'react-router-dom';
import { showError, showSuccess } from 'utils/utils';
import { validate } from 'utils/validation';
import useRecaptcha from 'hooks/useRecaptcha';
import withErrorBoundary from './withErrorBoundary';
import { MainContext } from 'contexts/MainContext';
import { b2b, defaultUserSiteConfig } from 'constants/generic';

const LoginPage = () => {
  const [t] = useTranslation();
  const { setUser, setUserSiteConfig } = useContext(MainContext);
  const navigate = useNavigate();
  const [form, setForm] = useState(new CredentialsModel());
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { handleRecapcha } = useRecaptcha();
  const location = useLocation();

  useEffect(() => {
    setForm(new CredentialsModel());
    setIsSubmitted(false);
  }, [location.pathname]);

  const onSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setIsSubmitted(true);
    const recaptchaToken = await handleRecapcha();
    if (!recaptchaToken) return;

    if (validate(validation, form)) {
      setIsLoading(true);

      const user = await login({ ...form, recaptchaToken }, defaultUserSiteConfig);

      if (user) {
        navigate('/');
        showSuccess(t('login.success'));
        setUser(user);

        if (user.config) {
          setUserSiteConfig({
            ...user.config,
            hotel: { ...user.config.hotel, displayStyle: '2', areRoomsFiltered: false },
          });
          setForm(new CredentialsModel());
        }
      } else {
        showError(t('login.error'));
      }

      setIsLoading(false);
    }
  };

  return (
    <form className="login-wrapper" onSubmit={onSubmit}>
      <div className="title mb-30">{t('login.login_your')}</div>
      <FormGroup
        onChange={(username) => setForm({ ...form, username })}
        value={form.username}
        label={t(b2b ? 'login.usercode' : 'login.username')}
        validation={validation.username}
        isSubmitted={isSubmitted}
        name="username"
      />
      <FormGroup
        onChange={(password) => setForm({ ...form, password })}
        value={form.password}
        label={t('login.password')}
        validation={validation.password}
        isSubmitted={isSubmitted}
        type="password"
        name="password"
      />
      <Button
        title={t('login.login')}
        className="btn btn-primary w100p btn-st"
        disabled={isLoading}
      />
    </form>
  );
};

export default withErrorBoundary(LoginPage, 'LoginPage');
